import React from "react";
import L from "leaflet";
import {
  MapTextConfig,
  colorConfigs,
  disabledRegionColor,
  initialZoom,
  maxZoom,
} from "./map-configs";
import { SubtitleRange, MapConfig, MapControls, Position } from "./types";

export const getKeyFromFeatureObj = ({ market_to_ }: any) => market_to_;

export const styleState = (feature: any) => {
  return {
    color: "#0D0D0D",
    weight: 2,
    opacity: 1,
    fillOpacity: 1,
    fillColor: disabledRegionColor,
  };
};

export const styleRegions = ({
  data,
  rangeGrades,
}: {
  data: any;
  rangeGrades: SubtitleRange[];
}) => {
  return ({ properties }: any) => {
    const key = getKeyFromFeatureObj(properties);
    const regionIntensiy = data[key]?.value;
    return {
      weight: 0.3,
      opacity: 1,
      color: "#000000",
      fillOpacity: 0.85,
      fillColor: getColor({ rangeGrades, intensity: regionIntensiy }),
    };
  };
};

const findInRange = (
  rangeGrades: SubtitleRange[],
  value: number
): SubtitleRange | undefined => {
  if (!value && value !== 0) return;
  const range = rangeGrades.find(
    (grade) => value >= grade.min && value <= grade.max
  );
  return range;
};

export const getColor = ({
  rangeGrades,
  intensity: value,
}: {
  rangeGrades: SubtitleRange[];
  intensity: number;
}) => {
  const range = findInRange(rangeGrades, value);
  if (range) {
    return range.color;
  }

  return disabledRegionColor;
};

export const getLevel = ({
  rangeGrades,
  intensity: value,
}: {
  rangeGrades: SubtitleRange[];
  intensity: number;
}) => {
  const range = findInRange(rangeGrades, value);

  if (range) return range.level;
  const lastRange = rangeGrades[rangeGrades.length - 1];

  return lastRange.level;
};

export const disableMapNavigation = ({ map }: { map: L.Map }) => {
  map.dragging.disable();
  map.touchZoom.disable();
  map.doubleClickZoom.disable();
  map.scrollWheelZoom.disable();
  map.boxZoom.disable();
  map.keyboard.disable();
  if (map.tap) map.tap.disable();
};

export const isInsideUSA = ({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}) => {
  // http://en.wikipedia.org/wiki/Extreme_points_of_the_United_States#Westernmost
  const top = 49.3457868; // north lat
  const left = -124.7844079; // west long
  const right = -66.9513812; // east long
  const bottom = 24.7433195; // south lat
  return (
    bottom <= latitude &&
    top >= latitude &&
    left <= longitude &&
    longitude <= right
  );
};

export const createInfoboxControl = ({
  infoRef,
  mapRef,
  data,
  multipleMaps,
  selectedMap,
}: {
  selectedMap: MapConfig;
  multipleMaps: boolean;
  infoRef: React.MutableRefObject<any | undefined>;
  mapRef: React.MutableRefObject<L.Map | undefined>;
  data: any;
}) => {
  const infoBoxDefaultText = "Select a region";
  if (!infoRef.current) {
    // @ts-ignore
    infoRef.current = L.control({ position: "topleft" });

    infoRef.current!.onAdd = function () {
      const classNames = `map-control map-infobox${
        multipleMaps ? " map-infobox-multiple-maps" : ""
      }`;

      this._div = L.DomUtil.create("div", classNames);

      this._div.innerHTML = `<h2>${MapTextConfig[selectedMap]?.infoBox}</h2> <p>${infoBoxDefaultText}</p>`;

      return this._div;
    };

    infoRef.current.addTo(mapRef.current);
  }

  if (infoRef.current) {
    infoRef.current!.updateText = function ({
      text,
      value,
      city,
    }: {
      text: string;
      value?: string | null;
      city?: string;
    }) {
      const hasRegion = value !== "Region unavailable";
      const title = `<h2>${text} </h2>`;
      if (value && hasRegion) {
        return (this._div.innerHTML = `${title}<h3>${city}'s region</h3> <p>${value}<p>`);
      }
      if (!hasRegion) {
        return (this._div.innerHTML = `${title}<p>${value}<p>`);
      }

      return (this._div.innerHTML = `${title} <p>${infoBoxDefaultText}<p>`);
    };

    infoRef.current!.update = function (props: any) {
      if (props && data) {
        const key = getKeyFromFeatureObj(props);
        const selectedRegion = data[key];
        const hasValue =
          selectedRegion &&
          (Boolean(selectedRegion.value) || selectedRegion.value === 0);

        if (!selectedRegion || !hasValue) {
          return infoRef.current!.updateText({
            text: MapTextConfig[selectedMap]?.infoBox,
            value: "Region unavailable",
          });
        }

        const { value, city } = selectedRegion;
        const level = getLevelFromValue({ map: selectedMap, value });

        return infoRef.current!.updateText({
          text: MapTextConfig[selectedMap]?.infoBox,
          city,
          value: addIntensityLevel({
            text: MapTextConfig[selectedMap]!.getValue({ value }),
            level,
          }),
        });
      }
      return infoRef.current!.updateText({
        text: MapTextConfig[selectedMap]?.infoBox,
      });
    };
  }
};

export const createControls = ({
  controlsRef,
  mapRef,
  onChange,
  geoJsonRef,
  infoRef,
  maps,
  selectedMap,
}: {
  maps: MapConfig[];
  controlsRef: React.MutableRefObject<MapControls | undefined>;
  infoRef: React.MutableRefObject<any | undefined>;
  mapRef: React.MutableRefObject<L.Map | undefined>;
  geoJsonRef: React.MutableRefObject<any | undefined>;
  onChange: (option: MapConfig) => void;
  selectedMap: MapConfig;
}) => {
  const createSingleControl = (controlName: MapConfig) => {
    const selectedClass = "map-select-control--selected";

    if (!controlsRef.current![controlName]) {
      // @ts-ignore
      controlsRef.current[controlName] = L.control();
      controlsRef.current![controlName].onAdd = function (map: L.Map) {
        const classNames = `map-select-control ${
          selectedMap === controlName ? selectedClass : ""
        }`;
        this._div = L.DomUtil.create("div", classNames);
        this._div.innerHTML = MapTextConfig[controlName]?.label;
        this._div.setAttribute("controlName", controlName);

        return this._div;
      };
    }

    controlsRef.current![controlName].addTo(mapRef.current);

    L.DomEvent.on(controlsRef.current![controlName]._div, "click", (e) => {
      L.DomEvent.stopPropagation(e);
      geoJsonRef.current.resetStyle(e.target);
      // @ts-ignore
      const newControlName = e.target.getAttribute("controlName") as MapConfig;

      infoRef.current.updateText({
        text: MapTextConfig[newControlName]?.infoBox,
      });

      maps.forEach((map) => {
        L.DomUtil.removeClass(controlsRef.current![map]._div, selectedClass);
      });

      onChange(newControlName);
      L.DomUtil.addClass(
        controlsRef.current![newControlName]._div,
        selectedClass
      );
    });
  };

  if (!controlsRef.current) {
    controlsRef.current = {};
  }
  if (maps.length > 1) {
    maps.forEach((map) => {
      createSingleControl(map);
    });
  }
};

const zoomLevels = 1;
const zoomDelta = (maxZoom - initialZoom) / zoomLevels;

const getZoom = ({
  isStatic,
  initialZoom,
}: {
  isStatic: boolean;
  initialZoom: number;
}) => {
  if (!isStatic) return initialZoom;
  const windowWidth = window.innerWidth;
  const tabledWidth = 768;
  const desktopWidth = 1024;
  const desktopLargeWidth = 1440;
  const fourKWidth = 2048;

  if (windowWidth <= tabledWidth) return 2.8;
  if (windowWidth <= desktopWidth) return 3;
  if (windowWidth <= desktopLargeWidth) return 3.5;
  if (windowWidth <= fourKWidth) return 3.7;

  return 4;
};

export const createMap = ({
  mapRef,
  isStatic,
  initialFocus,
  initialZoom,
  id,
}: {
  mapRef: React.MutableRefObject<L.Map | undefined>;
  isStatic: boolean;
  initialFocus: Position;
  initialZoom: number;
  id: string;
}) => {
  if (!mapRef.current) {
    mapRef.current = L.map(id, {
      zoomControl: false,
      attributionControl: false,
      zoomSnap: 0.1,
      zoomDelta,
      preferCanvas: true,
    });

    if (!isStatic) {
      new L.Control.Zoom({ position: "bottomright" }).addTo(mapRef.current);
    }
  }
  const zoom = getZoom({ isStatic, initialZoom });
  mapRef.current.setView([initialFocus.lat, initialFocus.lng], zoom);

  if (!isStatic) {
    L.tileLayer("http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom,
      minZoom: initialZoom,
    }).addTo(mapRef.current);
  }
};

export const getRandomMap = ({ maps }: { maps: MapConfig[] }): MapConfig => {
  const min = 0;
  const max = maps.length - 1;

  const rand = Math.round(min + Math.random() * (max - min));
  return maps[rand];
};

export const addIntensityLevel = ({
  text,
  level,
}: {
  text: string | null;
  level?: string;
}) => {
  if (level) {
    return `${level} (${text})`;
  }
  return text;
};

export const getLevelFromValue = ({
  map,
  value,
}: {
  map: MapConfig;
  value: number;
}) => {
  const range = colorConfigs[map];
  const config = findInRange(range, value);
  return config?.level;
};
export const createGlobalZoom = ({
  mapRef,
  focus,
  globalZoomRef,
  hasValidLocation,
}: {
  mapRef: React.MutableRefObject<L.Map | undefined>;
  focus: Position;
  globalZoomRef: React.MutableRefObject<any>;
  hasValidLocation: boolean;
}) => {
  if (globalZoomRef.current) {
    mapRef.current?.removeControl(globalZoomRef.current);
  }
  if (hasValidLocation) {
    // @ts-ignore
    const globalZoom = L.control({ position: "bottomright" });
    globalZoom.onAdd = function (map: any) {
      this._div = L.DomUtil.create("div", "toggle-map-zoom leaflet-bar");
      this._div.innerHTML +=
        '<a href="#" title="Return to national view" class="glyphicon-globe"></a>';
      L.DomEvent.on(this._div, "click", (e) => {
        map.setView(new L.LatLng(focus.lat, focus.lng), maxZoom);
        L.DomEvent.stopPropagation(e);
      });
      return this._div;
    };

    globalZoom.addTo(mapRef.current);
    globalZoomRef.current = globalZoom;
  }
};
